import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, Coins, TrendingDown, ShoppingBag, MessageSquare, Loader2, Zap, Gift, Flame,
} from "lucide-react";

const MODEL_LABEL = {
  "gemini-3.1-pro-preview": "Gemini 3.1 Pro",
  "gpt-5.4": "GPT-5.4",
  "claude-sonnet-4-6": "Claude Sonnet 4.6",
};
const MODEL_DOT = {
  "gemini-3.1-pro-preview": "#4285F4",
  "gpt-5.4": "#10A37F",
  "claude-sonnet-4-6": "#D97757",
};

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function Usage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/usage/history").then((r) => setData(r.data)).catch(() => setData({ usage: [], purchases: [] }));
  }, []);

  const stats = [
    { icon: Coins, label: "Current balance", value: data?.unlimited ? "∞" : (data?.credits ?? 0).toLocaleString(), sub: "credits" },
    { icon: TrendingDown, label: "Credits spent", value: (data?.total_spent ?? 0).toLocaleString(), sub: `${data?.total_messages ?? 0} messages` },
    { icon: ShoppingBag, label: "Credits purchased", value: (data?.total_purchased ?? 0).toLocaleString(), sub: `₹${(data?.total_spent_inr ?? 0).toLocaleString()} spent` },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] blur-[140px] opacity-20" style={{ background: "radial-gradient(circle, #00FF66 0%, transparent 70%)" }} />

      <header className="border-b border-white/10 nig-glass relative z-20">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            data-testid="usage-back"
            onClick={() => navigate("/app")}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to app
          </button>
          <button
            data-testid="usage-buy-credits"
            onClick={() => navigate("/billing")}
            className="px-4 py-2 rounded-full bg-[#00FF66] hover:bg-[#00CC52] text-black text-sm font-semibold transition-colors flex items-center gap-1.5"
          >
            <Zap className="w-4 h-4" /> Buy credits
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-14 relative z-10">
        <h1 className="font-heading font-extrabold text-4xl tracking-tighter">Usage & history</h1>
        <p className="text-zinc-400 mt-2">Track your balance, spending and everything you've built.</p>

        {!data ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-[#00FF66] animate-spin" />
          </div>
        ) : (
          <>
            {!data.unlimited && data.streak > 0 && (
              <div data-testid="streak-banner" className="flex items-center gap-4 rounded-2xl border border-[#00FF66]/20 bg-[#101a12] p-5 mt-8">
                <div className="w-12 h-12 rounded-xl bg-[#00FF66]/10 flex items-center justify-center shrink-0">
                  <Flame className="w-6 h-6 text-[#00FF66]" />
                </div>
                <div className="flex-1">
                  <p className="font-heading font-bold text-lg text-white">{data.streak}-day streak 🔥</p>
                  <p className="text-sm text-zinc-400">
                    Come back tomorrow for <span className="text-[#00FF66] font-semibold">+{Math.min(10 + data.streak * 5, 50)} free credits</span>. Streaks grow up to +50/day.
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              {stats.map((s, i) => (
                <div key={i} className="p-6 rounded-2xl bg-[#141415] border border-white/10">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <s.icon className="w-4 h-4 text-[#00FF66]" /> {s.label}
                  </div>
                  <div className="font-heading font-extrabold text-4xl tracking-tighter text-white mt-3">{s.value}</div>
                  <div className="text-xs text-zinc-500 mt-1">{s.sub}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
              {/* Recent activity */}
              <div>
                <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#00FF66]" /> Recent activity
                </h2>
                <div className="rounded-2xl border border-white/10 bg-[#141415] divide-y divide-white/5" data-testid="usage-activity-list">
                  {data.usage.length === 0 && (
                    <p className="text-sm text-zinc-500 p-6 text-center">No activity yet. Start a chat to see it here.</p>
                  )}
                  {data.usage.map((u, i) => (
                    <button
                      key={i}
                      onClick={() => navigate("/app")}
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#1F1F22] transition-colors"
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: MODEL_DOT[u.model] || "#00FF66" }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{u.title || "Chat"}</p>
                        <p className="text-xs text-zinc-500">{MODEL_LABEL[u.model] || u.model} · {fmt(u.created_at)}</p>
                      </div>
                      <span className={`text-sm font-mono shrink-0 ${u.charged ? "text-red-400" : "text-zinc-500"}`}>
                        {u.charged ? `-${u.cost}` : "free"}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Purchases */}
              <div>
                <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-[#00FF66]" /> Purchases
                </h2>
                <div className="rounded-2xl border border-white/10 bg-[#141415] divide-y divide-white/5" data-testid="usage-purchases-list">
                  {data.purchases.length === 0 && (
                    <p className="text-sm text-zinc-500 p-6 text-center">No purchases yet.</p>
                  )}
                  {data.purchases.map((t, i) => (
                    <div key={i} className="flex items-center gap-3 p-4">
                      <div className="w-8 h-8 rounded-lg bg-[#00FF66]/10 flex items-center justify-center shrink-0">
                        {t.kind === "daily" ? <Gift className="w-4 h-4 text-[#00FF66]" /> : <Coins className="w-4 h-4 text-[#00FF66]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white">
                          {t.credits.toLocaleString()} credits{t.kind === "daily" ? " · daily bonus" : ""}
                        </p>
                        <p className="text-xs text-zinc-500">{fmt(t.created_at)}</p>
                      </div>
                      <span className="text-sm font-mono text-[#00FF66] shrink-0">
                        {t.kind === "daily" ? "free" : `+₹${t.amount}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
